import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error, already_verified
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link');
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/auth/verify-email?token=${token}`, {
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        if (data.already_verified) {
          setStatus('already_verified');
          setMessage('Your email is already verified');
        } else {
          setStatus('success');
          setMessage('Email verified successfully!');
        }
        setEmail(data.email || '');
      } else {
        setStatus('error');
        setMessage(data.detail || 'Verification failed');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Failed to verify email. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex flex-col items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        {/* Logo */}
        <div className="mb-6">
          <img
            src="/images/logo.png"
            alt="SmartReadGo Logo"
            className="w-24 h-24 mx-auto object-contain"
          />
        </div>

        {/* Status Icon */}
        {status === 'verifying' && (
          <div className="mb-6">
            <Loader2 className="w-16 h-16 mx-auto text-blue-700 animate-spin" />
          </div>
        )}

        {status === 'success' && (
          <div className="mb-6">
            <CheckCircle className="w-16 h-16 mx-auto text-green-600" />
          </div>
        )}

        {(status === 'error' || status === 'already_verified') && (
          <div className="mb-6">
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
              status === 'already_verified' ? 'bg-blue-100' : 'bg-red-100'
            }`}>
              {status === 'already_verified' ? (
                <CheckCircle className="w-10 h-10 text-blue-600" />
              ) : (
                <XCircle className="w-10 h-10 text-red-600" />
              )}
            </div>
          </div>
        )}

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          {status === 'verifying' && 'Verifying Your Email...'}
          {status === 'success' && 'Email Verified!'}
          {status === 'already_verified' && 'Already Verified'}
          {status === 'error' && 'Verification Failed'}
        </h1>

        {/* Message */}
        <p className="text-gray-600 dark:text-gray-300 mb-8">
          {message}
          {email && <span className="block mt-2 font-medium text-gray-900 dark:text-white">{email}</span>}
        </p>

        {/* Actions */}
        <div className="space-y-3">
          {(status === 'success' || status === 'already_verified') && (
            <Button
              onClick={() => navigate('/')}
              className="w-full bg-blue-700 hover:bg-blue-800 text-white py-6 text-lg font-semibold rounded-full"
            >
              Go to Home
            </Button>
          )}

          {status === 'error' && (
            <>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-blue-700 hover:bg-blue-800 text-white py-6 text-lg font-semibold rounded-full"
              >
                Go to Login
              </Button>
              <Button
                onClick={() => navigate('/signup')}
                variant="outline"
                className="w-full border-gray-300 text-gray-700 dark:text-gray-200 py-6 text-lg font-semibold rounded-full"
              >
                Create New Account
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
